import { useState } from 'react';
import {
  Button,
  Caption1,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Textarea,
  tokens,
} from '@fluentui/react-components';
import { useApi } from '../api/useApi';
import { useAsync } from '../api/useAsync';
import { PageHeader } from '../components/PageHeader';
import { GroupList } from '../features/groups/GroupList';
import { GroupDetailView } from '../features/groups/GroupDetailView';
import type { GroupSummaryDto } from '../api/types';
import '../features/groups/groups.css';

interface ResultRow {
  group: GroupSummaryDto;
  status: 'Executed' | 'PendingApproval' | 'Error';
  message?: string;
}

export function GroupsDeletePage() {
  const api = useApi();
  const policies = useAsync(() => api.getPagePolicies(), [api]);
  const ticketMode = policies.data?.find((p) => p.pageKey === 'groups.delete')?.ticketMode ?? 'None';

  const [selected, setSelected] = useState<GroupSummaryDto[]>([]);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | undefined>();
  const [ticket, setTicket] = useState('');
  const [ticketError, setTicketError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<ResultRow[] | null>(null);

  const selectedIds = new Set(selected.map((g) => g.id));
  const totalMembers = selected.reduce((sum, g) => sum + g.memberCount, 0);

  function toggle(g: GroupSummaryDto) {
    setSelected((prev) => (prev.some((x) => x.id === g.id) ? prev.filter((x) => x.id !== g.id) : [...prev, g]));
  }

  function reset() {
    setSelected([]);
    setReason('');
    setTicket('');
    setResults(null);
    setReasonError(undefined);
    setTicketError(undefined);
  }

  async function submit() {
    if (selected.length === 0) return;
    if (!reason.trim()) {
      setReasonError('Ein Grund ist erforderlich.');
      return;
    }
    if (ticketMode === 'Required' && !ticket.trim()) {
      setTicketError('Ticketnummer ist erforderlich.');
      return;
    }
    setReasonError(undefined);
    setTicketError(undefined);
    setBusy(true);

    const rows: ResultRow[] = [];
    for (const g of selected) {
      try {
        const res = await api.deleteGroup(g.id, { reason: reason.trim(), ticketNumber: ticket.trim() || undefined });
        rows.push({
          group: g,
          status: res.status === 'PendingApproval' ? 'PendingApproval' : 'Executed',
          message: res.message ?? undefined,
        });
      } catch (e) {
        rows.push({ group: g, status: 'Error', message: e instanceof Error ? e.message : String(e) });
      }
    }
    setResults(rows);
    setBusy(false);
  }

  if (results) {
    return (
      <div>
        <PageHeader title="Gruppen löschen" />
        <div style={{ maxWidth: '640px', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
          {results.map((r) => (
            <MessageBar
              key={r.group.id}
              intent={r.status === 'Executed' ? 'success' : r.status === 'PendingApproval' ? 'info' : 'error'}
            >
              <MessageBarBody>
                <MessageBarTitle>{r.group.displayName}</MessageBarTitle>
                {r.status === 'PendingApproval'
                  ? 'Freigabepflichtig — Antrag eingereicht (siehe „Meine Anfragen").'
                  : r.status === 'Executed'
                    ? (r.message ?? 'Gelöscht.')
                    : r.message}
              </MessageBarBody>
            </MessageBar>
          ))}
          <div>
            <Button appearance="primary" onClick={reset}>
              Weitere Löschung
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Gruppen löschen"
        description="Gruppen links wählen, Grund angeben und löschen. Standardmäßig freigabepflichtig (4-Augen)."
      />
      <div className="grp-layout">
        <GroupList selected={selectedIds} onToggle={toggle} />

        <div className="grp-detail-stack">
          {selected.length === 0 ? (
            <div className="grp-panel">
              <div className="grp-empty">Wähle links eine oder mehrere Gruppen zum Löschen.</div>
            </div>
          ) : (
            <>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                {selected.length} {selected.length === 1 ? 'Gruppe' : 'Gruppen'} ausgewählt · {totalMembers} Mitglieder
                gesamt
              </Caption1>
              {selected.map((g) => (
                <GroupDetailView key={g.id} group={g} />
              ))}

              <MessageBar intent="warning">
                <MessageBarBody>
                  <MessageBarTitle>Achtung</MessageBarTitle>
                  Das Löschen entfernt die Gruppe samt Mitgliedschaften. Microsoft-365-Gruppen verbleiben 30 Tage im
                  Papierkorb und sind in dieser Zeit wiederherstellbar.
                </MessageBarBody>
              </MessageBar>

              <Field
                label="Grund"
                required
                validationMessage={reasonError}
                hint="Pflicht — wird im Audit-Log und ggf. im Freigabe-Antrag protokolliert."
              >
                <Textarea
                  value={reason}
                  onChange={(_, d) => setReason(d.value)}
                  rows={3}
                  placeholder="Warum wird die Gruppe gelöscht?"
                />
              </Field>

              {ticketMode !== 'None' ? (
                <Field
                  label="Ticketnummer"
                  required={ticketMode === 'Required'}
                  validationMessage={ticketError}
                  hint={ticketMode === 'Required' ? 'Pflicht für diese Aktion.' : 'Optional.'}
                >
                  <Input value={ticket} onChange={(_, d) => setTicket(d.value)} placeholder="z. B. INC0012345" />
                </Field>
              ) : null}

              <div>
                <Button
                  appearance="primary"
                  style={{ backgroundColor: tokens.colorPaletteRedBackground3 }}
                  disabled={busy || !reason.trim()}
                  onClick={submit}
                >
                  {busy
                    ? 'Bitte warten…'
                    : selected.length > 1
                      ? `${selected.length} Gruppen löschen`
                      : 'Gruppe löschen'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
